import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createRequestedUserSchema } from "@tyto/shared/src/schemas";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { PageTopNav } from "@/components/nav/PageTopNav";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router";

// Types
const schema = createRequestedUserSchema;
type FormValues = z.infer<typeof schema>;

export function OrgInviteUser() {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      setError(null);
      setSuccess(null);
      const res = await fetch(`/api/org/${orgId}/inviteUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || "Failed to invite user");
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(data.message || "User invited successfully!");
      queryClient.invalidateQueries({
        queryKey: ['invitedUsers', orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['organizationUsers', orgId],
      });
      navigate(`/app/org/${orgId}/users`);
      form.reset();
    },
    onError: (err: any) => {
      setError(err.message || "Failed to invite user");
    },
  });

  const onSubmit = (values: FormValues) => {
    mutation.mutate(values);
  };

  return (
    <div className="max-w-md mx-auto mt-8">
      <PageTopNav pageName="Invite User" />
      <p className="mb-8 text-muted-foreground">Invite a user to your organization. User will receive an email invitation to join. User will be added to your organisation with a 'User' role. Their role can be changed after joining.</p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <Input type="email" placeholder="user@email.com" {...field} />
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full" disabled={mutation.status === 'pending'}>
            {mutation.status === 'pending' ? "Inviting..." : "Invite User"}
          </Button>
        </form>
      </Form>
      {success && <div className="mt-4 text-green-600">{success}</div>}
      {error && <div className="mt-4 text-red-600">{error}</div>}
    </div>
  );
}
